import { FC, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useUser } from "@framework/provider-user";
import { IUser } from "@framework/types";
import { useAlarmColor } from "../../../../../../../../hooks/use-alarm-color";
import { ITrainControlsResponse } from "../../interfaces";
import { useBlinkAnimation } from "../use-blink-animation";
import { formatMeasurementNumber } from "../../../../utils/measurement-format";

interface ITiAngularGaugeProps {
  control: ITrainControlsResponse;
  flashUnacknowledgedAlarms: boolean;
  flashFaultAlarms: boolean;
}

function parseNumericValue(raw: string | null): number {
  if (raw == null || raw === "") return 0;
  const n = parseFloat(String(raw).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

function getScaleFallbackDigits(min: number, max: number): number {
  const span = max - min;
  if (span >= 20) return 0;
  if (span >= 2) return 1;
  return 2;
}

function angleRad(frac: number): number {
  return Math.PI + frac * (Math.PI / 2);
}

function polar(cx: number, cy: number, r: number, frac: number): { x: number; y: number } {
  const θ = angleRad(frac);
  return { x: cx + r * Math.cos(θ), y: cy + r * Math.sin(θ) };
}

function sectorRing(cx: number, cy: number, rOut: number, rIn: number): string {
  const t0 = angleRad(0);
  const t1 = angleRad(1);
  const x0 = cx + rOut * Math.cos(t0);
  const y0 = cy + rOut * Math.sin(t0);
  const x1 = cx + rOut * Math.cos(t1);
  const y1 = cy + rOut * Math.sin(t1);
  const x2 = cx + rIn * Math.cos(t1);
  const y2 = cy + rIn * Math.sin(t1);
  const x3 = cx + rIn * Math.cos(t0);
  const y3 = cy + rIn * Math.sin(t0);
  return `M ${x0} ${y0} A ${rOut} ${rOut} 0 0 1 ${x1} ${y1} L ${x2} ${y2} A ${rIn} ${rIn} 0 0 0 ${x3} ${y3} Z`;
}

const VIEW_W = 120;
const VIEW_H = 120;
const CX = 100;
const CY = 100;
const R_OUT = 90;
const R_IN = 56;
const R_TICK_IN = 70;
const R_TICK_MAJOR = 98;
const R_TICK_MINOR = 88;
const R_LABEL = 62;
const NEEDLE_LEN = 78;
const PIVOT_R = 5;

export const TiAngularGauge: FC<ITiAngularGaugeProps> = ({ control, flashUnacknowledgedAlarms, flashFaultAlarms }) => {
  const { value, props, alarmStatus, unacknowledgedEvent } = control;
  const p = props as Record<string, unknown>;
  const { profile } = useUser<IUser>();

  const minRaw = p.MinValue ?? p.Min ?? p.mMin;
  const maxRaw = p.MaxValue ?? p.Max ?? p.mMax;
  const min = typeof minRaw === "number" && Number.isFinite(minRaw) ? minRaw : 0;
  const max = typeof maxRaw === "number" && Number.isFinite(maxRaw) && maxRaw > min ? maxRaw : 100;

  const numeric = useMemo(() => parseNumericValue(value), [value]);
  const valueFrac = useMemo(() => {
    if (max <= min) return 0;
    return Math.min(1, Math.max(0, (numeric - min) / (max - min)));
  }, [numeric, min, max]);

  const majorFracs = useMemo(() => [0, 0.25, 0.5, 0.75, 1] as const, []);
  const scaleFallbackDigits = useMemo(() => getScaleFallbackDigits(min, max), [min, max]);
  const minorFracs = useMemo(() => {
    const out: number[] = [];
    for (let i = 1; i < 20; i++) {
      if (i % 5 === 0) continue;
      out.push(i / 20);
    }
    return out;
  }, []);

  const alarmTint = useAlarmColor(Number(alarmStatus) || 0);
  const needleColor = alarmStatus > 0 ? alarmTint : "#000000";

  const shouldBlinkBg = flashUnacknowledgedAlarms && unacknowledgedEvent > 0;
  const shouldBlinkBorder = flashFaultAlarms && unacknowledgedEvent === 2;
  const { animation, keyframes } = useBlinkAnimation(shouldBlinkBg, shouldBlinkBorder, alarmTint);

  const w = Number(props.Width) || 100;
  const h = Number(props.Height) || 100;
  const showText = w >= 56 && h >= 72;

  const ringPath = useMemo(() => sectorRing(CX, CY, R_OUT, R_IN), []);
  const needleTip = useMemo(() => polar(CX, CY, NEEDLE_LEN, valueFrac), [valueFrac]);

  return (
    <Box
      sx={{
        position: "absolute",
        left: props.Left,
        top: props.Top,
        width: props.Width,
        height: props.Height,
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        justifyContent: "flex-start",
        boxSizing: "border-box",
        border: "1px solid #37474f",
        borderRadius: "4px",
        backgroundColor: "rgba(255,255,255,0.92)",
        overflow: "hidden",
        p: "2px",
        gap: showText ? "2px" : 0,
        minWidth: 0,
        minHeight: 0,
        animation,
        ...keyframes,
      }}
    >
      <Box
        sx={{
          position: "relative",
          flex: "1 1 0",
          minHeight: 0,
          minWidth: 0,
          width: 0,
          height: "100%",
          alignSelf: "stretch",
        }}
      >
        <Box
          component="svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMinYMax meet"
          shapeRendering="geometricPrecision"
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            width: "100%",
            height: "100%",
            display: "block",
          }}
          aria-hidden
        >
          <path d={ringPath} fill="#eceff1" stroke="none" />

          {minorFracs.map((f, i) => {
            const a = polar(CX, CY, R_TICK_IN, f);
            const b = polar(CX, CY, R_TICK_MINOR, f);
            return (
              <line
                key={`m-${i}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke="#424242"
                strokeWidth={1}
                strokeLinecap="butt"
              />
            );
          })}

          {majorFracs.map((f, i) => {
            const a = polar(CX, CY, R_TICK_IN, f);
            const b = polar(CX, CY, R_TICK_MAJOR, f);
            const labelVal = min + f * (max - min);
            const lp = polar(CX, CY, R_LABEL, f);
            return (
              <g key={`M-${i}`}>
                <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} stroke="#000000" strokeWidth={1} strokeLinecap="butt" />
                <text
                  x={lp.x}
                  y={lp.y}
                  fill="#000000"
                  fontSize={13}
                  fontFamily="system-ui, -apple-system, Segoe UI, sans-serif"
                  fontWeight={700}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  style={{ userSelect: "none" }}
                >
                  {formatMeasurementNumber(labelVal, control.valueUnits ?? null, profile.interfaceSettings.dataFormat, {
                    fallbackDigits: scaleFallbackDigits,
                  })}
                </text>
              </g>
            );
          })}

          <line
            x1={CX}
            y1={CY}
            x2={needleTip.x}
            y2={needleTip.y}
            stroke={needleColor}
            strokeWidth={2.25}
            strokeLinecap="round"
          />
          <circle cx={CX} cy={CY} r={PIVOT_R} fill="#000000" />
        </Box>
      </Box>
      {showText && (
        <Typography
          variant="caption"
          component="div"
          sx={{
            flex: "0 0 auto",
            alignSelf: "stretch",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: Math.min(12, Math.max(8, w / 9)),
            fontWeight: 700,
            lineHeight: 1.05,
            color: "#263238",
            px: "2px",
            maxWidth: "40%",
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            textAlign: "center",
          }}
          title={value ?? undefined}
        >
          {value ?? "—"}
        </Typography>
      )}
    </Box>
  );
};
